import { Guid } from "@common/guid";
import { LegalEntityFieldOrderItem } from "./LegalEntities.types";

// Mirror of Backend/Core/Filtering/LegalEntities/LegalEntityColumn.cs. Values are explicit and must stay in step:
// they travel in the search request, in sortByColumn and in the saved-view blobs the backend serves.
export enum LegalEntityColumn {
    Id = 0,
    Name = 1,
    Country = 2,
    BusinessAreas = 3,
    ComplianceAreas = 4,
    Certifications = 5,
    Responsible = 6,
    Assets = 7,
    Approved = 8,
    Status = 9,
    Types = 10,
    Tags = 11,
    Classifications = 12,
    LatestAudit = 13,
    LatestAuditStatus = 14,
    RiskAreas = 15,
    PreselectedOnCreate = 16,
    Description = 17,
    Address = 18,
    ZipCode = 19,
    City = 20,
    CompanyNumber = 21,
    ReferenceNumber = 22,
    TransferImpactAssessmentNotNecessary = 23,
    GroupCompany = 24,
    SupervisionType = 25,
    SupervisionFrequency = 26,
    Location = 27,
    TransferBasis = 28,
    DataProcessor = 29,
    SubProcessor = 30,
    RecipientOnDisclosure = 31,
    SenderOnSource = 32,
    SenderOnDataProcessor = 33,
    Documents = 34,
    HasDocuments = 35,
    DocumentTypes = 36,
    DocumentOwners = 37,
    DocumentGroupEntities = 38,
    Contacts = 39,
    // 40 was InternalContractParties, dropped from the overview by decision on 2026-09-16.
    ProcessingActivities = 41,
    NumberOfProcessingActivities = 42,
    DataCategories = 43,
    NumberOfDataCategories = 44,
    DataSubjects = 45,
    NumberOfDataSubjects = 46,
    Roles = 47,
    NumberOfSharings = 48,
    Note = 49,
    Website = 50,
    SubContractors = 51,
    TransferBasisNotNecessary = 52,
    UserFriendlyId = 53,
}

// The string ids the column picker, the table headers and user-saved views work in. Same role as the
// filterIdToEnumMap the system and contract pages fetch, but static: legal entities have no custom fields, so
// the vocabulary is known up front.
export const LegalEntityColumnId = {
    userFriendlyId: "userFriendlyId",
    name: "name",
    country: "country",
    businessAreas: "businessAreas",
    complianceAreas: "complianceAreas",
    certifications: "certifications",
    responsible: "responsible",
    assets: "assets",
    approved: "approved",
    status: "status",
    types: "types",
    tags: "tags",
    classifications: "classifications",
    latestAudit: "latestAudit",
    latestAuditStatus: "latestAuditStatus",
    riskAreas: "riskAreas",
    preselectedOnCreate: "preselectedOnCreate",
    description: "description",
    address: "address",
    zipCode: "zipCode",
    city: "city",
    companyNumber: "companyNumber",
    referenceNumber: "referenceNumber",
    tiaNotNecessary: "tiaNotNecessary",
    groupCompany: "groupCompany",
    supervisionType: "supervisionType",
    supervisionFrequency: "supervisionFrequency",
    location: "location",
    transferBasis: "transferBasis",
    dataProcessor: "dataProcessor",
    subProcessor: "subProcessor",
    recipientOnDisclosure: "recipientOnDisclosure",
    senderOnSource: "senderOnSource",
    senderOnDataProcessor: "senderOnDataProcessor",
    documents: "documents",
    hasDocuments: "hasDocuments",
    documentTypes: "documentTypes",
    documentOwners: "documentOwners",
    documentGroupEntities: "documentGroupEntities",
    contacts: "contacts",
    processingActivities: "processingActivities",
    numberOfProcessingActivities: "numberOfProcessingActivities",
    dataCategories: "dataCategories",
    numberOfDataCategories: "numberOfDataCategories",
    dataSubjects: "dataSubjects",
    numberOfDataSubjects: "numberOfDataSubjects",
    roles: "roles",
    numberOfSharings: "numberOfSharings",
    note: "note",
    website: "website",
    subContractors: "subContractors",
    transferBasisNotNecessary: "transferBasisNotNecessary",
} as const;

export const legalEntityColumnByFilterId: Record<string, LegalEntityColumn> = {
    [LegalEntityColumnId.userFriendlyId]: LegalEntityColumn.UserFriendlyId,
    [LegalEntityColumnId.name]: LegalEntityColumn.Name,
    [LegalEntityColumnId.country]: LegalEntityColumn.Country,
    [LegalEntityColumnId.businessAreas]: LegalEntityColumn.BusinessAreas,
    [LegalEntityColumnId.complianceAreas]: LegalEntityColumn.ComplianceAreas,
    [LegalEntityColumnId.certifications]: LegalEntityColumn.Certifications,
    [LegalEntityColumnId.responsible]: LegalEntityColumn.Responsible,
    [LegalEntityColumnId.assets]: LegalEntityColumn.Assets,
    [LegalEntityColumnId.approved]: LegalEntityColumn.Approved,
    [LegalEntityColumnId.status]: LegalEntityColumn.Status,
    [LegalEntityColumnId.types]: LegalEntityColumn.Types,
    [LegalEntityColumnId.tags]: LegalEntityColumn.Tags,
    [LegalEntityColumnId.classifications]: LegalEntityColumn.Classifications,
    [LegalEntityColumnId.latestAudit]: LegalEntityColumn.LatestAudit,
    [LegalEntityColumnId.latestAuditStatus]: LegalEntityColumn.LatestAuditStatus,
    [LegalEntityColumnId.riskAreas]: LegalEntityColumn.RiskAreas,
    [LegalEntityColumnId.preselectedOnCreate]: LegalEntityColumn.PreselectedOnCreate,
    [LegalEntityColumnId.description]: LegalEntityColumn.Description,
    [LegalEntityColumnId.address]: LegalEntityColumn.Address,
    [LegalEntityColumnId.zipCode]: LegalEntityColumn.ZipCode,
    [LegalEntityColumnId.city]: LegalEntityColumn.City,
    [LegalEntityColumnId.companyNumber]: LegalEntityColumn.CompanyNumber,
    [LegalEntityColumnId.referenceNumber]: LegalEntityColumn.ReferenceNumber,
    [LegalEntityColumnId.tiaNotNecessary]: LegalEntityColumn.TransferImpactAssessmentNotNecessary,
    [LegalEntityColumnId.groupCompany]: LegalEntityColumn.GroupCompany,
    [LegalEntityColumnId.supervisionType]: LegalEntityColumn.SupervisionType,
    [LegalEntityColumnId.supervisionFrequency]: LegalEntityColumn.SupervisionFrequency,
    [LegalEntityColumnId.location]: LegalEntityColumn.Location,
    [LegalEntityColumnId.transferBasis]: LegalEntityColumn.TransferBasis,
    [LegalEntityColumnId.dataProcessor]: LegalEntityColumn.DataProcessor,
    [LegalEntityColumnId.subProcessor]: LegalEntityColumn.SubProcessor,
    [LegalEntityColumnId.recipientOnDisclosure]: LegalEntityColumn.RecipientOnDisclosure,
    [LegalEntityColumnId.senderOnSource]: LegalEntityColumn.SenderOnSource,
    [LegalEntityColumnId.senderOnDataProcessor]: LegalEntityColumn.SenderOnDataProcessor,
    [LegalEntityColumnId.documents]: LegalEntityColumn.Documents,
    [LegalEntityColumnId.hasDocuments]: LegalEntityColumn.HasDocuments,
    [LegalEntityColumnId.documentTypes]: LegalEntityColumn.DocumentTypes,
    [LegalEntityColumnId.documentOwners]: LegalEntityColumn.DocumentOwners,
    [LegalEntityColumnId.documentGroupEntities]: LegalEntityColumn.DocumentGroupEntities,
    [LegalEntityColumnId.contacts]: LegalEntityColumn.Contacts,
    [LegalEntityColumnId.processingActivities]: LegalEntityColumn.ProcessingActivities,
    [LegalEntityColumnId.numberOfProcessingActivities]: LegalEntityColumn.NumberOfProcessingActivities,
    [LegalEntityColumnId.dataCategories]: LegalEntityColumn.DataCategories,
    [LegalEntityColumnId.numberOfDataCategories]: LegalEntityColumn.NumberOfDataCategories,
    [LegalEntityColumnId.dataSubjects]: LegalEntityColumn.DataSubjects,
    [LegalEntityColumnId.numberOfDataSubjects]: LegalEntityColumn.NumberOfDataSubjects,
    [LegalEntityColumnId.roles]: LegalEntityColumn.Roles,
    [LegalEntityColumnId.numberOfSharings]: LegalEntityColumn.NumberOfSharings,
    [LegalEntityColumnId.note]: LegalEntityColumn.Note,
    [LegalEntityColumnId.website]: LegalEntityColumn.Website,
    [LegalEntityColumnId.subContractors]: LegalEntityColumn.SubContractors,
    [LegalEntityColumnId.transferBasisNotNecessary]: LegalEntityColumn.TransferBasisNotNecessary,
};

// Derived from the map above rather than written out a second time, so the two cannot fall out of step.
const legalEntityFilterIdByColumn: Record<number, string> = Object.fromEntries(
    Object.entries(legalEntityColumnByFilterId).map(([filterId, column]) => [column, filterId])
);

export function getLegalEntityFilterIdByColumn(column: LegalEntityColumn): string | undefined {
    return legalEntityFilterIdByColumn[column];
}

// A display field id is either one of the names above or the bare guid of a template custom field, which is how
// the system and contract overviews tell the two apart as well. Order is the order the user arranged them in.
export function toLegalEntityDisplayFields(filterIds: Array<string>): Array<LegalEntityFieldOrderItem> {
    return filterIds
        .map((filterId, index) =>
            Guid.isGuid(filterId) ? { customFieldId: filterId, displayOrder: index } : { column: legalEntityColumnByFilterId[filterId], displayOrder: index }
        )
        .filter((field) => field.customFieldId !== undefined || field.column !== undefined);
}
